<template>
  <v-form @submit.prevent="$emit('save', page)">
    <v-card>
      <v-card-title>
        {{ $t('edit_page') }}
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="page.title"
              :error-messages="errors['title']"
              :label="formMixin_getRequiredFieldLabel($t('title'))"
              name="title"
              autofocus
              @input="formMixin_clearErrors('title')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-if="page.type !== 'trainings'"
              v-model="page.parent_id"
              :items="items"
              :label="$t('choose_parent_id')"
              :loading="loadingPages"
              :error-messages="errors['parent_id']"
              item-text="title"
              item-value="id"
              clearable
              @input="formMixin_clearErrors('parent_id')"
            />
          </v-col>

          <v-col v-if="!page.parent_id" cols="12" sm="6">
            <v-radio-group
              v-model="page.type"
              :error-messages="errors['type']"
              name="type"
              :label="formMixin_getRequiredFieldLabel($t('type'))"
              @change="formMixin_clearErrors('type')"
            >
              <template v-slot:label>
                <span class="v-label">{{ formMixin_getRequiredFieldLabel($t('type')) }}</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon color="grey lighten-1" class="ml-2" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>{{ $t('pages_tooltip_text') }}</span>
                </v-tooltip>
              </template>
              <v-radio
                v-for="item in patientStoryTypes"
                :key="item.label"
                :label="$t(item.label)"
                :value="item.label"
              ></v-radio>
            </v-radio-group>
          </v-col>

          <v-col v-if="parentType === 'projects'" cols="12" sm="6">
            <v-text-field
              v-if="page.parent_id"
              v-model="page.start_at"
              :error-messages="errors['start_at']"
              :label="formMixin_getRequiredFieldLabel($t('project_start_at'))"
              name="start_at"
              @input="formMixin_clearErrors('start_at')"
            />
            <v-text-field
              v-if="page.parent_id"
              v-model="page.end_at"
              :error-messages="errors['end_at']"
              :label="formMixin_getRequiredFieldLabel($t('project_end_at'))"
              name="end_at"
              @input="formMixin_clearErrors('project_end_at')"
            />

            <div class="mt-4 mb-4">
              <div class="pdf-report-label">
                <p>{{ $t('pdf_report_drop_zone_message') }}</p>
              </div>

              <BaseFileDropZone
                v-if="!page.pdf_report"
                accept=".pdf"
                class="d-block"
                @drop="$emit('upload:pdf-report', $event, 'pdf_report', modelType, page.id)"
              >
                <div class="pdf-report-drop-zone">
                  <!--                  <div class="drop-zone-message">-->
                  <!--                    <p>{{ $t('file_drop_zone_message') }}</p>-->
                  <!--                  </div>-->
                </div>
              </BaseFileDropZone>

              <div v-else class="pl-10">
                <v-icon size="80px" class="patient-story-img">
                  mdi-file
                </v-icon>
                <div class="d-flex justify-space-between name-box" style="width: 100px;">
                  <p v-if="page.pdf_report" class="image-name text-truncate">
                    {{ page.pdf_report.original_name }}
                  </p>
                  <v-btn icon @click="$emit('delete:pdf-report', page.pdf_report)"
                    ><v-icon>mdi-delete</v-icon></v-btn
                  >
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" sm="6">
            <v-checkbox
              v-model="page.is_published"
              :error-messages="errors['is_published']"
              :label="$t('published')"
              @change="formMixin_clearErrors('is_published')"
            />
            <v-checkbox
              v-if="page.type === 'projects'"
              v-model="page.show_in_support"
              :error-messages="errors['show_in_support']"
              :label="$t('show_in_support')"
              @change="formMixin_clearErrors('show_in_support')"
            />
            <v-checkbox
              v-if="page.type === 'projects'"
              v-model="page.is_active"
              :error-messages="errors['is_active']"
              :label="$t('active')"
              @change="formMixin_clearErrors('is_active')"
            />
            <v-checkbox
              v-if="page.type === 'projects'"
              v-model="page.is_period_visible"
              :error-messages="errors['is_period_visible']"
              :label="$t('is_period_visible')"
              @change="formMixin_clearErrors('is_period_visible')"
            />
          </v-col>

          <v-col cols="12" v-if="page.type === 'projects'">
            <div class="label">
              <p>{{ $t('multiple_image_drop_zone_message') }}</p>
            </div>
            <BaseFileDropZone
              accept=".jpeg,.jpg,.bmp,.png,.gif"
              @drop="onImageUpload($event, 'image', modelType, page.id)"
              multiple
            >
              <div class="drop-zone">
                <div v-for="img in page.images" class="pl-5" :key="img.id">
                  <v-img
                    :src="$backendUrl(img.image_path)"
                    :alt="img.original_name"
                    width="100px"
                    height="100px"
                    class="pages-img"
                  />
                  <div class="d-flex justify-space-between name-box">
                    <p class="image-name text-truncate">
                      {{ img.original_name }}
                    </p>
                    <v-btn icon @click.stop="onImageDelete(img)"><v-icon>mdi-delete</v-icon></v-btn>
                  </div>
                </div>
              </div>
            </BaseFileDropZone>
          </v-col>

          <v-col v-if="page.type === 'projects'" cols="12" class="mt-4 mb-4">
            <div class="label">
              <p>{{ $t('multiple_partner_logo_drop_zone_message') }}</p>
            </div>
            <BaseFileDropZone
              accept=".jpeg,.jpg,.bmp,.png,.gif"
              multiple
              @drop="onImageUpload($event, 'partner_logo', modelType, page.id)"
            >
              <div class="drop-zone">
                <div v-for="img in page.partner_logos" class="pl-5" :key="img.id">
                  <v-img
                    :src="$backendUrl(img.image_path)"
                    :alt="img.original_name"
                    width="100px"
                    height="100px"
                    class="pages-img"
                  />
                  <div class="d-flex justify-space-between name-box">
                    <p class="image-name text-truncate">
                      {{ img.original_name }}
                    </p>
                    <v-btn icon @click.stop="onImageDelete(img)"><v-icon>mdi-delete</v-icon></v-btn>
                  </div>
                </div>
              </div>
            </BaseFileDropZone>
          </v-col>

          <v-col cols="12">
            <BaseTextEditor
              v-if="page.parent_id || page.type === 'trainings'"
              v-model="page.body"
              :error-messages="errors['body']"
              :label="formMixin_getRequiredFieldLabel($t('body'))"
              name="body"
              @input="formMixin_clearErrors('body')"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <span class="text-subtitle-2 ml-3"> * {{ $t('must_be_filled') }} </span>

        <v-spacer />

        <v-btn
          v-if="page.parent_id || page.type === 'trainings'"
          text
          color="secondary"
          @click="$emit('preview', page)"
        >
          {{ $t('preview') }}
        </v-btn>

        <v-btn text color="primary" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn :disabled="disabled" :loading="disabled" color="primary" text type="submit">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import formMixin from '../mixins/form-mixin';
import BaseFileDropZone from './base/BaseFileDropZone';
import BaseTextEditor from './base/BaseTextEditor';

export default {
  name: 'PagesForm',

  components: { BaseTextEditor, BaseFileDropZone },

  mixins: [formMixin],

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    page: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loadingPages: {
      type: Boolean,
      default: false,
    },
    modelType: {
      type: String,
      default: 'App\\Models\\Page',
    },
    parentType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      patientStoryTypes: [
        { label: 'projects' },
        { label: 'trainings' },
        { label: 'preterm_birth' },
        { label: 'premature_baby' },
        { label: 'already_at_home' },
        { label: 'lost_baby' },
      ],

      menuTypes: [{ label: 'info' }, { label: 'help' }],
    };
  },

  methods: {
    onImageUpload(images, type, modelType, modelId) {
      this.$emit('upload:image', images, type, modelType, modelId);
    },

    onImageDelete(image) {
      this.$emit('delete:image', image);
    },
  },
};
</script>

<style scoped>
.drop-zone {
  background-color: white;
  outline: 2px dashed darkgray;
  outline-offset: 10px;
  width: 97%;
  height: auto;
  min-height: 120px;
  text-align: center;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.label {
  flex: none;
  margin-bottom: 20px;
  background-color: white;
  z-index: 2000;
}
.label p,
.pdf-report-label p,
.image-name {
  color: darkgray;
  margin: 0;
}
.pages-img {
  margin-top: 16px;
}
.name-box {
  width: 120px;
  display: flex !important;
  align-items: center;
}
.pdf-report-drop-zone {
  background-color: white;
  outline: 2px dashed darkgray;
  width: 97%;
  height: 100px;
  text-align: center;
  /*margin: auto;*/
  position: relative;
}
.pdf-report-label {
  flex: none;
  margin-bottom: 10px;
  background-color: white;
  z-index: 2000;
}
</style>
